import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Typography, Grid, Spin, Breadcrumb } from 'antd';
import { Seo, JsonLd } from '../seo';

const { Title, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const API_BASE = process.env.REACT_APP_API_BASE || '';
const BRAND = 'var(--brand-primary, #222957)';
const SITE_ORIGIN = 'https://shiningstars.tw';

// 知識文章詳情頁：內容由後台 /api/content/knowledge/:slug 供應（含 body_html、上一篇/下一篇）。
export default function GuideArticle() {
  const { slug } = useParams();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setNotFound(false);
    window.scrollTo(0, 0);
    fetch(`${API_BASE}/api/content/knowledge/${slug}`)
      .then((r) => {
        if (r.status === 404) { setNotFound(true); return null; }
        return r.ok ? r.json() : null;
      })
      .then((d) => { if (alive) setArticle(d); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  // 找不到文章 → 回知識首頁
  useEffect(() => {
    if (notFound) navigate('/crystal-guide', { replace: true });
  }, [notFound, navigate]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '80px 0' }}><Spin /></div>;
  }
  if (!article) return null;

  const url = `${SITE_ORIGIN}/crystal-guide/${slug}`;
  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.seo_description,
    inLanguage: 'zh-Hant-TW',
    author: { '@type': 'Organization', name: '星河耀眼 ShiningStar Jewelry' },
    publisher: {
      '@type': 'Organization',
      name: '星河耀眼 ShiningStar Jewelry',
      logo: { '@type': 'ImageObject', url: `${SITE_ORIGIN}/logo.png` },
    },
    mainEntityOfPage: url,
    ...(article.updated ? { dateModified: article.updated } : {}),
  };

  return (
    <div style={{ width: '100%', minHeight: '100%', background: isMobile ? '#fff' : '#F9FAFD' }}>
      <Seo
        title={article.seo_title}
        description={article.seo_description}
        path={`/crystal-guide/${slug}`}
        type="article"
      />
      <JsonLd id="guide-article" data={articleLd} />

      <style>{`
        .guide-body { color: #2C3674; font-size: ${isMobile ? 15 : 16}px; line-height: 1.95; }
        .guide-body h2 { color: ${BRAND}; font-size: ${isMobile ? 19 : 22}px; font-weight: 800; margin: 28px 0 10px; }
        .guide-body h3 { color: #242C5E; font-size: ${isMobile ? 16 : 18}px; font-weight: 700; margin: 20px 0 8px; }
        .guide-body p { margin: 0 0 14px; }
        .guide-body ul, .guide-body ol { margin: 0 0 16px; padding-left: 22px; }
        .guide-body li { margin: 0 0 8px; }
        .guide-body a { color: ${BRAND}; text-decoration: underline; }
        .guide-body nav { margin-top: 32px; padding-top: 18px; border-top: 1px solid #E5E7F5; display: flex; flex-wrap: wrap; gap: 12px; justify-content: space-between; }
      `}</style>

      <div style={{ maxWidth: 760, margin: '0 auto', padding: isMobile ? '20px 18px 48px' : '40px 32px 80px' }}>
        <Breadcrumb
          style={{ marginBottom: 16 }}
          items={[
            { title: <Link to="/">首頁</Link> },
            { title: <Link to="/crystal-guide">水晶入門指南</Link> },
            { title: article.title },
          ]}
        />
        <Title level={1} style={{ fontSize: isMobile ? 24 : 32, color: '#1C224A', fontWeight: 800, lineHeight: 1.35, marginBottom: 6 }}>
          {article.title}
        </Title>
        {article.summary && (
          <Paragraph type="secondary" style={{ fontSize: isMobile ? 14 : 16, marginBottom: 24 }}>
            {article.summary}
          </Paragraph>
        )}

        {/* body_html 來自後台（站方撰寫，受信任內容） */}
        <div className="guide-body" dangerouslySetInnerHTML={{ __html: article.body_html || '' }} />

        {/* 上一篇 / 下一篇 */}
        {(article.prev || article.next) && (
          <nav style={{ marginTop: 32, paddingTop: 18, borderTop: '1px solid #E5E7F5', display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between' }}>
            {article.prev
              ? <Link to={`/crystal-guide/${article.prev.slug}`} style={{ color: BRAND }}>← {article.prev.title}</Link>
              : <span />}
            {article.next
              ? <Link to={`/crystal-guide/${article.next.slug}`} style={{ color: BRAND }}>{article.next.title} →</Link>
              : <span />}
          </nav>
        )}
      </div>
    </div>
  );
}
